import { Controller } from "@hotwired/stimulus"

// Controls the calendar grid in Plantafel (section toggling, scrolling)
// Persists collapsed/expanded state to localStorage
export default class extends Controller {
  static targets = [
    "appointmentsContent",
    "appointmentsIcon",
    "appointmentsSection",
    "sickNotesContent",
    "sickNotesIcon",
    "sickNotesSection",
    "leaveRequestsContent",
    "leaveRequestsIcon",
    "leaveRequestsSection",
  ]

  static STORAGE_KEY = "plantafel_section_states"

  connect() {
    // Load saved state from localStorage, default to expanded/visible
    const savedState = this.loadState()
    this.appointmentsExpanded = savedState.appointments !== false
    this.sickNotesExpanded = savedState.sickNotes !== false
    this.leaveRequestsExpanded = savedState.leaveRequests !== false

    // Apply saved state to UI (collapsible content)
    this.applyState()

    // Also apply section visibility (for filter-based hiding)
    this.applySectionVisibility("appointments", this.appointmentsExpanded)
    this.applySectionVisibility("sickNotes", this.sickNotesExpanded)
    this.applySectionVisibility("leaveRequests", this.leaveRequestsExpanded)

    // Listen for section visibility changes from filter panel
    this.boundHandleVisibilityChange = this.handleVisibilityChange.bind(this)
    this.boundHandleVisibilityLoaded = this.handleVisibilityLoaded.bind(this)
    window.addEventListener(
      "plantafel:section-visibility-changed",
      this.boundHandleVisibilityChange,
    )
    window.addEventListener("plantafel:section-visibility-loaded", this.boundHandleVisibilityLoaded)
  }

  disconnect() {
    window.removeEventListener(
      "plantafel:section-visibility-changed",
      this.boundHandleVisibilityChange,
    )
    window.removeEventListener(
      "plantafel:section-visibility-loaded",
      this.boundHandleVisibilityLoaded,
    )
  }

  // Load state from localStorage
  loadState() {
    try {
      const saved = localStorage.getItem(this.constructor.STORAGE_KEY)
      return saved ? JSON.parse(saved) : {}
    } catch (_e) {
      return {}
    }
  }

  // Save state to localStorage
  saveState() {
    try {
      const state = {
        appointments: this.appointmentsExpanded,
        sickNotes: this.sickNotesExpanded,
        leaveRequests: this.leaveRequestsExpanded,
      }
      localStorage.setItem(this.constructor.STORAGE_KEY, JSON.stringify(state))
    } catch (_e) {
      // Ignore storage errors
    }
  }

  // Apply current state to UI elements
  applyState() {
    // Apply appointments state
    if (this.hasAppointmentsContentTarget) {
      this.appointmentsContentTarget.classList.toggle("hidden", !this.appointmentsExpanded)
    }
    if (this.hasAppointmentsIconTarget) {
      this.appointmentsIconTarget.style.transform = this.appointmentsExpanded
        ? "rotate(0deg)"
        : "rotate(-90deg)"
    }

    // Apply sick notes state
    if (this.hasSickNotesContentTarget) {
      this.sickNotesContentTarget.classList.toggle("hidden", !this.sickNotesExpanded)
    }
    if (this.hasSickNotesIconTarget) {
      this.sickNotesIconTarget.style.transform = this.sickNotesExpanded
        ? "rotate(0deg)"
        : "rotate(-90deg)"
    }

    // Apply leave requests state
    if (this.hasLeaveRequestsContentTarget) {
      this.leaveRequestsContentTarget.classList.toggle("hidden", !this.leaveRequestsExpanded)
    }
    if (this.hasLeaveRequestsIconTarget) {
      this.leaveRequestsIconTarget.style.transform = this.leaveRequestsExpanded
        ? "rotate(0deg)"
        : "rotate(-90deg)"
    }
  }

  // Toggle section visibility
  toggleSection(event) {
    const section = event.currentTarget.dataset.section

    if (section === "appointments") {
      this.toggleAppointments()
    } else if (section === "sickNotes") {
      this.toggleSickNotes()
    } else if (section === "leaveRequests") {
      this.toggleLeaveRequests()
    }
  }

  // Toggle appointments section
  toggleAppointments() {
    this.appointmentsExpanded = !this.appointmentsExpanded

    if (this.hasAppointmentsContentTarget) {
      this.appointmentsContentTarget.classList.toggle("hidden", !this.appointmentsExpanded)
    }

    if (this.hasAppointmentsIconTarget) {
      this.appointmentsIconTarget.style.transform = this.appointmentsExpanded
        ? "rotate(0deg)"
        : "rotate(-90deg)"
    }

    this.saveState()
  }

  // Toggle sick notes section
  toggleSickNotes() {
    this.sickNotesExpanded = !this.sickNotesExpanded

    if (this.hasSickNotesContentTarget) {
      this.sickNotesContentTarget.classList.toggle("hidden", !this.sickNotesExpanded)
    }

    if (this.hasSickNotesIconTarget) {
      this.sickNotesIconTarget.style.transform = this.sickNotesExpanded
        ? "rotate(0deg)"
        : "rotate(-90deg)"
    }

    this.saveState()
  }

  // Toggle leave requests section
  toggleLeaveRequests() {
    this.leaveRequestsExpanded = !this.leaveRequestsExpanded

    if (this.hasLeaveRequestsContentTarget) {
      this.leaveRequestsContentTarget.classList.toggle("hidden", !this.leaveRequestsExpanded)
    }

    if (this.hasLeaveRequestsIconTarget) {
      this.leaveRequestsIconTarget.style.transform = this.leaveRequestsExpanded
        ? "rotate(0deg)"
        : "rotate(-90deg)"
    }

    this.saveState()
  }

  // Scroll to today in the grid
  scrollToToday() {
    const todayColumn = this.element.querySelector('[data-today="true"]')
    if (todayColumn) {
      todayColumn.scrollIntoView({ behavior: "smooth", inline: "center" })
    }
  }

  // Handle visibility change event from filter panel
  handleVisibilityChange(event) {
    const { section, visible } = event.detail
    this.applySectionVisibility(section, visible)
  }

  // Handle initial visibility state loaded from filter panel
  handleVisibilityLoaded(event) {
    const { state } = event.detail
    this.applySectionVisibility("appointments", state.appointments !== false)
    this.applySectionVisibility("sickNotes", state.sickNotes !== false)
    this.applySectionVisibility("leaveRequests", state.leaveRequests !== false)
  }

  // Apply visibility to a specific section
  applySectionVisibility(section, visible) {
    let sectionElement

    switch (section) {
      case "appointments":
        sectionElement = this.hasAppointmentsSectionTarget
          ? this.appointmentsSectionTarget
          : this.element.querySelector('[data-section-type="appointments"]')
        break
      case "sickNotes":
        sectionElement = this.hasSickNotesSectionTarget
          ? this.sickNotesSectionTarget
          : this.element.querySelector('[data-section-type="sickNotes"]')
        break
      case "leaveRequests":
        sectionElement = this.hasLeaveRequestsSectionTarget
          ? this.leaveRequestsSectionTarget
          : this.element.querySelector('[data-section-type="leaveRequests"]')
        break
    }

    if (sectionElement) {
      sectionElement.classList.toggle("hidden", !visible)
    }
  }
}
